import { useState } from 'react';
import type {
  AdminSandbox,
  AdminSandboxGameUsage,
  AdminSandboxHistoryEntry,
  AdminSandboxPersonUsage,
  AdminSandboxTeamUsage,
} from '@boardflight/shared';
import { SandboxEndReason } from '@boardflight/shared';
import { api, post } from '../api.js';
import { Async, Filter, Panel, Time, duration, useAsync, useDebounced } from '../ui.js';

/**
 * Who is using a sandbox, for how long, on what, and why one ended (§4).
 *
 * The history begins the day the writes shipped and there is no backfill, so the screen says so
 * rather than drawing an empty table that reads as "nobody used it".
 */

/** How far back every usage table on this screen looks. One control, because it is one question. */
const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
] as const;

/**
 * Which grouping of the same hours is on screen.
 *
 * Three tabs over one range rather than three stacked tables: they are three answers to *one*
 * question — where did the compute go — and reading them side by side is how a per-team total gets
 * explained by one person or one game. Stacked, the third one is below the fold and never opened.
 */
const GROUPINGS = [
  { id: 'team', label: 'Per team' },
  { id: 'person', label: 'Per person' },
  { id: 'game', label: 'Per game' },
] as const;

type Grouping = (typeof GROUPINGS)[number]['id'];

/**
 * The ways a sandbox ends (§4.2), for the history filter.
 *
 * Listed rather than derived from the rows on screen, because a filter built from what is already
 * loaded can only ever offer the reasons that are already visible — which is exactly backwards for
 * a control whose purpose is finding the rare one. Read off the shared enum rather than copied, so
 * a new reason (a linked sandbox's `idle`) is offered the day it can happen.
 */
const END_REASONS = SandboxEndReason.options;

/** The windows "recently" can mean. `''` is all of it, which is what the panel loads with. */
const RECENCY = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Ended in the last hour' },
  { value: '24', label: 'Ended in the last 24h' },
  { value: '168', label: 'Ended in the last 7 days' },
  { value: '720', label: 'Ended in the last 30 days' },
] as const;

/**
 * The machine a session got: the word, and the image it resolved to (migration 0034).
 *
 * Both, because they can disagree and the disagreement is the whole reason the pair is stored — a
 * deployment whose `E2B_TEMPLATE_ID_LARGE` points at the medium image says `large` here over a
 * template id that is visibly the wrong one.
 *
 * A row from before the columns existed shows an em dash and nothing else. It is deliberately not
 * filled in from the team's current size: the size is read when a session starts and the column an
 * operator edits afterwards describes the team's *next* sandbox, so that guess would be wrong in
 * exactly the case somebody is looking this up to check.
 */
function Machine({ size, templateId }: { size: string | null; templateId: string | null }) {
  if (size === null && templateId === null) return <span className="muted">—</span>;
  return (
    <>
      <span>{size ?? 'unknown size'}</span>
      <div className="mono muted small">{templateId ?? 'no template recorded'}</div>
    </>
  );
}

/** What the history is narrowed to — here rather than in it, so a count elsewhere can set it. */
interface HistoryFilter {
  q: string;
  reason: string;
  since: string;
  /** A team's id, set only by a team's failed starts: there is no control for it. */
  team: string;
}

const NO_FILTER: HistoryFilter = { q: '', reason: '', since: '', team: '' };

/** A count of failed starts that opens the history at them, or nothing when there are none. */
function FailedStarts({ count, onOpen }: { count: number; onOpen: () => void }) {
  if (!count) return null;
  return (
    <button className="link" title="Show them under Recently ended" onClick={onOpen}>
      {count}
    </button>
  );
}

/** The table's own totals, under it. */
function TotalRow({ label, rows, before = 0 }: {
  label: string;
  rows: readonly { sessions: number; playtimeMs: number; staleStarts: number }[];
  /** Columns between the label and the sessions. */
  before?: number;
}) {
  if (rows.length < 2) return null;
  const sum = (pick: (row: (typeof rows)[number]) => number) => rows.reduce((total, row) => total + pick(row), 0);
  return (
    <tr className="total">
      <td>{label}</td>
      {Array.from({ length: before }, (_, i) => <td key={i} />)}
      <td className="num">{sum((row) => row.sessions)}</td>
      <td className="num">{duration(sum((row) => row.playtimeMs))}</td>
      <td className="num">{sum((row) => row.staleStarts) || ''}</td>
    </tr>
  );
}

export function Sandboxes() {
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState('30');
  const [grouping, setGrouping] = useState<Grouping>('team');
  const [history, setHistory] = useState<HistoryFilter>(NO_FILTER);
  // A failed start is a `stale_row`: show those, narrowed to whoever's count was pressed.
  const showFailed = (patch: Partial<HistoryFilter>) => {
    setHistory({ ...NO_FILTER, reason: 'stale_row', ...patch });
    document.getElementById('sandbox-history')?.scrollIntoView({ behavior: 'smooth' });
  };

  const live = useAsync(() => api<{ items: AdminSandbox[]; at: number }>('/v1/admin/sandboxes'), []);

  return (
    <>
      <Panel title="Running now" actions={<button onClick={live.reload}>Refresh</button>}>
        {error ? <p className="error">{error}</p> : null}
        <Async state={live} empty="No sandboxes running.">
          {(data) =>
            data.items.length === 0 ? (
              <p className="muted">No sandboxes running.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Team</th>
                      <th>Started by</th>
                      <th>Started</th>
                      <th className="num">Running for</th>
                      <th>Machine</th>
                      <th>Sandbox</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <LiveRow
                        key={row.playgroundId}
                        row={row}
                        onError={setError}
                        onEnded={live.reload}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </Panel>

      <Panel
        title="Hours"
        actions={
          <div className="filters">
            <div className="tri" role="group">
              {GROUPINGS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={entry.id === grouping ? 'tri-on' : undefined}
                  onClick={() => setGrouping(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>
            <Filter label="Range" value={days} onChange={setDays} options={RANGES} />
          </div>
        }
      >
        {/* Keyed on the grouping, so switching tabs remounts and each table owns its own read
            rather than three of them firing on every range change. */}
        {grouping === 'team' ? <TeamHours days={days} onFailed={showFailed} /> : null}
        {grouping === 'person' ? <PersonHours days={days} onFailed={showFailed} /> : null}
        {grouping === 'game' ? <GameHours days={days} onFailed={showFailed} /> : null}
      </Panel>

      <History onError={setError} filter={history} onFilter={setHistory} days={days} />
    </>
  );
}

/** One running sandbox, and the End button that frees its seat. */
function LiveRow({ row, onError, onEnded }: {
  row: AdminSandbox;
  onError: (message: string | null) => void;
  onEnded: () => void;
}) {
  async function end() {
    const note = window.prompt(
      `End the sandbox for "${row.appName ?? row.appId}" (@${row.startedByUsername ?? 'unknown'})?\n\n` +
        'The seat is freed and the deploy token is revoked immediately. The machine itself stops ' +
        'on its own timeout, within the hour.\n\nWhy? (recorded)',
    );
    if (note === null || note.trim() === '') return;
    onError(null);
    try {
      await post(`/v1/admin/sandboxes/${row.playgroundId}/end`, { note });
      onEnded();
    } catch (failure) {
      onError(String(failure));
    }
  }

  return (
    <tr>
      <td>
        {row.appName ?? <span className="mono muted">{row.appId}</span>}
        <div className="muted">
          {row.engine} · {row.status}
        </div>
      </td>
      <td className="muted">{row.teamName ?? row.teamId}</td>
      <td className="mono muted">{row.startedByUsername ? `@${row.startedByUsername}` : '—'}</td>
      <td className="muted">
        <Time at={row.startedAt} />
      </td>
      <td className="num">{duration(row.elapsedMs)}</td>
      <td>
        <Machine size={row.sandboxSize} templateId={row.templateId} />
      </td>
      <td className="mono muted">
        {row.sandboxId ?? '—'}
        {/* A seat held by a session nothing is measuring: a start that wrote the playground row
            and died before it wrote the usage row. */}
        {row.untracked ? <div className="warn">not tracked</div> : null}
      </td>
      <td>
        <button className="danger" onClick={() => void end()}>
          End
        </button>
      </td>
    </tr>
  );
}

/**
 * Hours per team — the number a **seat grant** should be argued from.
 *
 * Seats are a per-team column, so this is the only one of the three groupings that lines up with a
 * control an admin can actually turn. The other two explain a total; this one is spent against.
 */
function TeamHours({ days, onFailed }: { days: string; onFailed: (patch: Partial<HistoryFilter>) => void }) {
  const state = useAsync(
    () =>
      api<{ items: AdminSandboxTeamUsage[]; days: number }>(
        `/v1/admin/sandboxes/usage?days=${days}`,
      ),
    [days],
  );

  return (
    <Async state={state} empty="Nothing recorded yet.">
      {(data) => (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Team</th>
                <th className="num">Seats</th>
                <th className="num">Sessions</th>
                <th className="num">Time</th>
                <th className="num">Failed starts</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.teamId}>
                  <td>{row.teamName ?? <span className="mono muted">{row.teamId}</span>}</td>
                  <td className="num">{row.playgroundSeats ?? '—'}</td>
                  <td className="num">{row.sessions}</td>
                  <td className="num">{duration(row.playtimeMs)}</td>
                  {/* Held a seat, ran nothing. Counted apart rather than dropped: it is part of
                      what a team's quota was spent on, but it is not play. */}
                  <td className="num">
                    <FailedStarts count={row.staleStarts} onOpen={() => onFailed({ team: row.teamId })} />
                  </td>
                </tr>
              ))}
              <TotalRow label="Every team" rows={data.items} before={1} />
            </tbody>
          </table>
        </div>
      )}
    </Async>
  );
}

/**
 * Hours per person — who is actually spending it.
 *
 * The grouping the per-team table averages away: one team's twenty hours is a different
 * conversation depending on whether it is four people or one, and a seat count set from the total
 * alone is set blind to that.
 *
 * Handles, never names (§D7). The unattributed line is real compute with nobody to attribute it to
 * — a deleted account, or a start that died before it recorded who was asking — and it is shown
 * rather than dropped, labelled for what it is.
 */
function PersonHours({ days, onFailed }: { days: string; onFailed: (patch: Partial<HistoryFilter>) => void }) {
  const state = useAsync(
    () =>
      api<{ items: AdminSandboxPersonUsage[]; days: number }>(
        `/v1/admin/sandboxes/usage/people?days=${days}`,
      ),
    [days],
  );

  return (
    <Async state={state} empty="Nothing recorded yet.">
      {(data) => (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th className="num">Teams</th>
                <th className="num">Sessions</th>
                <th className="num">Time</th>
                <th className="num">Failed starts</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.userId ?? 'unattributed'}>
                  <td className="mono">
                    {row.username ? (
                      `@${row.username}`
                    ) : (
                      <span className="muted">no account (deleted, or a start that died)</span>
                    )}
                  </td>
                  <td className="num">{row.teams}</td>
                  <td className="num">{row.sessions}</td>
                  <td className="num">{duration(row.playtimeMs)}</td>
                  <td className="num">
                    <FailedStarts count={row.staleStarts} onOpen={() => onFailed({ q: row.username ?? '' })} />
                  </td>
                </tr>
              ))}
              <TotalRow label="Everybody" rows={data.items} before={1} />
            </tbody>
          </table>
        </div>
      )}
    </Async>
  );
}

/**
 * Hours per game — what the compute went into.
 *
 * Deliberately not the analytics ranking on the tab beside it: that one counts what *players* did
 * on a Board, this counts what a sandbox cost to build the thing. A game can top one and sit at the
 * bottom of the other, and which of the two it is happens to be the interesting question.
 */
function GameHours({ days, onFailed }: { days: string; onFailed: (patch: Partial<HistoryFilter>) => void }) {
  const state = useAsync(
    () =>
      api<{ items: AdminSandboxGameUsage[]; days: number }>(
        `/v1/admin/sandboxes/usage/games?days=${days}`,
      ),
    [days],
  );

  return (
    <Async state={state} empty="Nothing recorded yet.">
      {(data) => (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Game</th>
                <th>Team</th>
                <th className="num">Sessions</th>
                <th className="num">Time</th>
                <th className="num">Failed starts</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={`${row.appId}/${row.teamId}`}>
                  {/* `playground_sessions` has no foreign key on `app_id` on purpose — it outlives
                      the game somebody deleted — so a row whose app is gone renders as its id. */}
                  <td>{row.appName ?? <span className="mono muted">{row.appId}</span>}</td>
                  <td className="muted">{row.teamName ?? row.teamId}</td>
                  <td className="num">{row.sessions}</td>
                  <td className="num">{duration(row.playtimeMs)}</td>
                  <td className="num">
                    <FailedStarts count={row.staleStarts} onOpen={() => onFailed({ q: row.appName ?? '', team: row.teamId })} />
                  </td>
                </tr>
              ))}
              <TotalRow label="Every game" rows={data.items} before={1} />
            </tbody>
          </table>
        </div>
      )}
    </Async>
  );
}

/**
 * Sessions that have ended, filtered.
 *
 * Every filter goes to the route and none of them is applied here (see the route's own header):
 * this panel holds a hundred rows out of a table that grows for as long as anybody uses a
 * playground, so a client-side filter would answer "no `agentd_gone` sessions" when what it meant
 * was "none in this page".
 *
 * The recency filter is on `ended_at` rather than `started_at`, which is the point of it. "What
 * stopped in the last hour" is the question somebody asks after a cost alert, and a session that
 * began three days ago and ended ten minutes ago is one of the answers.
 */
function History({ onError, filter, onFilter, days }: {
  onError: (message: string | null) => void;
  filter: HistoryFilter;
  onFilter: (next: HistoryFilter) => void;
  /** The page's range, which the end-reason counts are over. */
  days: string;
}) {
  const { q, reason, since, team } = filter;
  const set = (patch: Partial<HistoryFilter>) => onFilter({ ...filter, ...patch });
  const search = useDebounced(q);

  const state = useAsync(
    () =>
      api<{ items: AdminSandboxHistoryEntry[]; nextCursor: string | null }>(
        `/v1/admin/sandboxes/history?limit=50&q=${encodeURIComponent(search)}` +
          `&reason=${reason}&since=${since}${team ? `&team=${team}` : ''}`,
      ),
    [search, reason, since, team],
  );
  const reasons = useAsync(
    () => api<{ items: { reason: string; sessions: number }[] }>(`/v1/admin/sandboxes/end-reasons?days=${days}`),
    [days],
  );

  const filtered = search !== '' || reason !== '' || since !== '' || team !== '';

  return (
    <div id="sandbox-history">
    <Panel
      title="Recently ended"
      actions={
        <div className="filters">
          <label className="filter">
            <span className="muted small">Search</span>
            <input
              type="text"
              value={q}
              placeholder="Game, team or handle"
              onChange={(e) => set({ q: e.target.value })}
            />
          </label>
          <Filter
            label="Ended"
            value={since}
            onChange={(next) => set({ since: next })}
            options={RECENCY.map((entry) => ({ value: entry.value, label: entry.label }))}
          />
          <Filter
            label="Reason"
            value={reason}
            onChange={(next) => set({ reason: next })}
            options={[
              { value: '', label: 'Any reason' },
              ...END_REASONS.map((value) => ({ value, label: value })),
            ]}
          />
          {filtered ? (
            <button
              onClick={() => {
                onFilter(NO_FILTER);
                onError(null);
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      }
    >
      {reasons.data && reasons.data.items.length > 0 ? (
        <div className="chips" aria-label="How sessions ended">
          <span className="muted small">Ended in the range</span>
          {reasons.data.items.map((row) =>
            row.reason === 'unknown' ? (
              <span key={row.reason} className="chip">unknown <b>{row.sessions}</b></span>
            ) : (
              <button
                key={row.reason}
                className={row.reason === reason ? 'chip on' : 'chip'}
                onClick={() => set({ reason: row.reason === reason ? '' : row.reason })}
              >
                {row.reason} <b>{row.sessions}</b>
              </button>
            ),
          )}
        </div>
      ) : null}
      <Async state={state} empty="Nothing recorded yet.">
        {(data) =>
          data.items.length === 0 ? (
            <p className="muted">
              {filtered ? (
                'No ended session matches this filter.'
              ) : (
                <>
                  Nothing recorded yet. This history begins when the console shipped — there is no
                  backfill, so sandboxes used before then are not here.
                </>
              )}
            </p>
          ) : (
            <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Team</th>
                    <th>Started by</th>
                    <th>Started</th>
                    <th>Ended</th>
                    <th className="num">Lasted</th>
                    <th>Machine</th>
                    <th>Ended because</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={row.id}>
                      <td>{row.appName ?? <span className="mono muted">{row.appId}</span>}</td>
                      <td className="muted">{row.teamName ?? row.teamId}</td>
                      <td className="mono muted">
                        {row.startedByUsername ? `@${row.startedByUsername}` : '—'}
                      </td>
                      <td className="muted">
                        <Time at={row.startedAt} />
                      </td>
                      <td className="muted">
                        <Time at={row.endedAt} />
                      </td>
                      <td className="num">
                        {duration(row.durationMs)}
                        {/* E2B reaps on its own timeout and nothing observes the moment, so an
                            `expired` row is only accurate to the cron's hour. Say so. */}
                        {row.endReason === 'expired' ? <div className="muted">± 1h</div> : null}
                      </td>
                      <td>
                        <Machine size={row.sandboxSize} templateId={row.templateId} />
                      </td>
                      <td>
                        <span className="pill">{row.endReason ?? 'unknown'}</span>
                        {row.endDetail && row.endReason === 'admin_kill' ? (
                          <div className="muted">{row.endDetail}</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Never a silent cap. Fifty rows is a page, and a page that says nothing about being
                one reads as "this is all of it" — which on a filter for a rare end reason is the
                difference between "it never happens" and "not in the last fifty". */}
            {data.nextCursor !== null ? (
              <p className="note">
                The 50 most recent{filtered ? ' matching' : ''} sessions. There are older ones —
                narrow the range or the reason to reach them.
              </p>
            ) : null}
            </>
          )
        }
      </Async>
    </Panel>
    </div>
  );
}
